import { styled } from '@mui/material';
import CriterionInstructions from 'components/CreateAssignment/CriterionInstructions';
import { useDialog } from 'contexts/dialog';
import { useSnackbar } from 'contexts/snackbar';
import { omit } from 'lodash';
import { memo, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Button from 'shell/components/Button';
import { DialogActions, DialogContent, DialogTitle } from 'shell/components/Dialog';
import Divider from 'shell/components/Divider';
import IconButton from 'shell/components/IconButton';
import Paper from 'shell/components/Paper';
import ResponsiveDialog from 'shell/components/ResponsiveDialog';
import RichTextArea from 'shell/components/RichTextArea';
import Stack from 'shell/components/Stack';
import Text from 'shell/components/Text';
import { CUSTOM_CONTENT_WORD_COUNT_LIMIT } from 'utils/constants';
import { getHTMLWordCount } from 'utils/helper';

const defaultErrors = {
    gradingCustomContext: '',
    criteria: {},
};

const EditGradingSettingsDialog = memo(({ open, form: data, onClose, onSave }) => {
    const { setDialog, closeDialog } = useDialog();
    const { t } = useTranslation('assignment');
    const { t: videoT } = useTranslation('videoGrading');
    const { message } = useSnackbar();

    const [form, setForm] = useState(null);
    const [errors, setErrors] = useState(defaultErrors);
    const [pendingDrafts, setPendingDrafts] = useState({});
    const hasPendingDrafts = Object.values(pendingDrafts).some(Boolean);

    const handleClose = () => {
        if (form.isDirty || hasPendingDrafts) {
            setDialog({
                open: true,
                message: t('common:dialog.unsaved-changes.title', 'Unsaved Changes'),
                description: t(
                    'common:dialog.unsaved-changes.description',
                    'If you exit now, any changes you have made to grading settings will be lost.'
                ),
                note: '',
                variant: 'destructive',
                actionButtonText: t('common:controls.exit', 'Exit'),
                cancelButtonText: t('common:controls.cancel', 'Cancel'),
                onAction: () => {
                    closeDialog();
                    onClose();
                },
                onCancel: () => closeDialog(),
                onClose: () => closeDialog(),
            });
        } else {
            onClose();
        }
    };

    const updateCriterion = useCallback((criterionId, nextCriterion) => {
        setForm((current) => ({
            ...current,
            criteria: { ...current.criteria, [criterionId]: nextCriterion },
            isDirty: true,
        }));
    }, []);

    const updatePendingDraft = useCallback((criterionId, hasDraft) => {
        setPendingDrafts((current) => {
            if (hasDraft) return { ...current, [criterionId]: true };
            const remaining = { ...current };
            delete remaining[criterionId];
            return remaining;
        });
    }, []);

    const showInstructionsTooLong = useCallback(() => {
        message.error(
            t('error.instructions-too-long', "Please make sure the instructions don't exceed {{wordCount}} words.", {
                wordCount: CUSTOM_CONTENT_WORD_COUNT_LIMIT,
            })
        );
    }, [message, t]);

    const isListOverLimit = (values) => getHTMLWordCount((values || []).join(' ')) > CUSTOM_CONTENT_WORD_COUNT_LIMIT;

    const handleListAdd = (criterionId, listKey, nextCriterion) => {
        if (!isListOverLimit(nextCriterion[listKey])) return true;
        setErrors((current) => ({
            ...current,
            criteria: {
                ...current.criteria,
                [criterionId]: { ...current.criteria[criterionId], [listKey]: true },
            },
        }));
        showInstructionsTooLong();
        return false;
    };

    const validateAll = () => {
        let isValid = true;

        setErrors({
            gradingCustomContext: getHTMLWordCount(form.gradingCustomContext) > CUSTOM_CONTENT_WORD_COUNT_LIMIT,
            criteria: Object.entries(form.criteria).reduce((acc, [criterionId, criterion]) => {
                return {
                    ...acc,
                    [criterionId]: {
                        grading_instructions:
                            getHTMLWordCount(criterion.grading_instructions || '') > CUSTOM_CONTENT_WORD_COUNT_LIMIT,
                        look_fors: isListOverLimit(criterion.look_fors),
                        violations_omissions: isListOverLimit(criterion.violations_omissions),
                    },
                };
            }, {}),
        });

        if (
            getHTMLWordCount(form.gradingCustomContext) > CUSTOM_CONTENT_WORD_COUNT_LIMIT ||
            Object.values(form.criteria).some(
                (criterion) =>
                    getHTMLWordCount(criterion.grading_instructions || '') > CUSTOM_CONTENT_WORD_COUNT_LIMIT ||
                    isListOverLimit(criterion.look_fors) ||
                    isListOverLimit(criterion.violations_omissions)
            )
        ) {
            isValid = false;
            showInstructionsTooLong();
        }

        return isValid;
    };

    const handleClickSave = () => {
        if (hasPendingDrafts) {
            message.error(
                videoT('pending-draft', 'Finish adding or cancel the instruction draft before saving these settings.')
            );
            return;
        }
        const isValid = validateAll();
        if (!isValid) return;
        onSave(omit(form, ['isDirty']));
        onClose();
    };

    useEffect(() => {
        if (open) {
            const criterionIds = [
                ...(data.rubric?.criteria || []).map(({ id }) => id),
                ...Object.keys(data.gradingSettings?.criteria || {}),
            ].filter((criterionId, index, values) => values.indexOf(criterionId) === index);
            setForm({
                gradingCustomContext: data.gradingCustomContext,
                criteria: criterionIds.reduce((acc, criterionId) => {
                    const existing = data.gradingSettings?.criteria?.[criterionId] || {};
                    return {
                        ...acc,
                        [criterionId]: {
                            ...existing,
                            grading_instructions: existing.grading_instructions ?? null,
                            look_fors: Array.isArray(existing.look_fors) ? existing.look_fors : [],
                            violations_omissions: Array.isArray(existing.violations_omissions)
                                ? existing.violations_omissions
                                : [],
                        },
                    };
                }, {}),
                isDirty: false,
            });
            setErrors({
                gradingCustomContext: '',
                criteria: {},
            });
            setPendingDrafts({});
        } else {
            const resetTimeout = setTimeout(() => {
                setForm(null);
                setErrors(defaultErrors);
            }, 150);
            return () => clearTimeout(resetTimeout);
        }
    }, [data.gradingCustomContext, data.gradingSettings, data.rubric?.criteria, open]);

    if (!form) return null;

    return (
        <CustomResponsiveDialog open={open} scroll={'paper'} onClose={handleClose}>
            <DialogTitle>
                <Stack alignItems={'center'} direction={'row'} justifyContent={'space-between'}>
                    <Text component={'h1'} variant={'h6'} weight={'medium'}>
                        {t('advanced-grading.title', 'Advanced Grading Settings')}
                    </Text>
                    <IconButton
                        aria-label={'close'}
                        kind={'exit'}
                        size={'medium'}
                        variant={'square'}
                        onClick={handleClose}
                    />
                </Stack>
            </DialogTitle>
            <Divider />
            <CustomDialogContent>
                <Stack spacing={2}>
                    <Text variant={'p1'} weight={'medium'}>
                        {t(
                            'feedback-settings.additional-instruction.grading-label',
                            'Additional Grading Instructions for the AI'
                        )}
                    </Text>
                    <CustomPaper variant={'outlined'}>
                        <Stack spacing={5}>
                            {data.rubric && (
                                <Text>
                                    {t(
                                        'advanced-grading.description',
                                        'In this section, you can give the AI extra guidance for its grading suggestions. These instructions can be general or tailored to each specific criterion.'
                                    )}
                                </Text>
                            )}
                            <RichTextArea
                                error={Boolean(errors.gradingCustomContext)}
                                helperText={t('remaining-word-count', '{{wordLimitStatus}} words', {
                                    wordLimitStatus: `${getHTMLWordCount(
                                        form.gradingCustomContext
                                    )}/${CUSTOM_CONTENT_WORD_COUNT_LIMIT}`,
                                })}
                                helperTextPosition={'end'}
                                label={t('advanced-grading.general-instructions', 'General Instructions')}
                                placeholder={t(
                                    'advanced-grading.grading-suggestions-placeholder',
                                    'Do you have any additional instructions about grading suggestions?'
                                )}
                                value={form.gradingCustomContext}
                                onChange={(value) =>
                                    setForm((current) => ({ ...current, gradingCustomContext: value, isDirty: true }))
                                }
                            />
                            <Stack spacing={2}>
                                <Text>
                                    {t('advanced-grading.criterion-instructions', 'Criterion-Specific Instructions')}
                                </Text>
                                <Stack spacing={4}>
                                    {!data.rubric ? (
                                        <Text color={'light'}>
                                            {t(
                                                'advanced-grading.criterion-instruction-missing-rubric',
                                                'Please create a rubric in “General Assignment Details” to enable criterion-specific grading instructions.'
                                            )}
                                        </Text>
                                    ) : (
                                        data.rubric.criteria.map((criterion) => (
                                            <CriterionInstructions
                                                key={criterion.id}
                                                criterion={criterion}
                                                error={errors.criteria[criterion.id]}
                                                instruction={form.criteria[criterion.id]}
                                                onChange={(nextCriterion) =>
                                                    updateCriterion(criterion.id, nextCriterion)
                                                }
                                                onDraftChange={(hasDraft) => updatePendingDraft(criterion.id, hasDraft)}
                                                onListAdd={(listKey, nextCriterion) =>
                                                    handleListAdd(criterion.id, listKey, nextCriterion)
                                                }
                                            />
                                        ))
                                    )}
                                </Stack>
                            </Stack>
                        </Stack>
                    </CustomPaper>
                </Stack>
            </CustomDialogContent>
            <Divider />
            <DialogActions>
                <Stack direction={'row'} spacing={4}>
                    <Button aria-hidden={false} color={'neutral'} variant={'tertiary'} onClick={handleClose}>
                        {t('common:controls.cancel', 'Cancel')}
                    </Button>
                    <Button onClick={handleClickSave}>{t('common:controls.save-settings', 'Save Settings')}</Button>
                </Stack>
            </DialogActions>
        </CustomResponsiveDialog>
    );
});

const CustomResponsiveDialog = styled(ResponsiveDialog)(() => {
    return {
        '& .MuiPaper-root > .MuiBox-root': {
            padding: 0,
        },
        '& .MuiDialog-paper': {
            maxWidth: '960px',
            width: '100%',
            height: '100%',
        },
    };
});

const CustomDialogContent = styled(DialogContent)(({ theme }) => {
    return {
        background: theme.palette.ensightful.neutral[50],
    };
});

const CustomPaper = styled(Paper)(({ theme }) => {
    return {
        maxWidth: '700px',
        padding: theme.spacing(5),
    };
});

export default EditGradingSettingsDialog;
